import { forwardRef, type FocusEvent, type InputHTMLAttributes } from "react";
import { useFieldEditSession } from "./field-edit-session.js";

export interface LeasedInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * The canonical target key this control edits, from `widgetTargetKey`.
   *
   * OPTIONAL, and absence is a real answer rather than an oversight: a control
   * whose value has no addressable target — one on an unmergeable kind, or a
   * row minted before its collection had stable ids — must claim NOTHING. A
   * guessed key would report a protection the executor can never honour, which
   * is worse than no protection because nobody would go looking for it.
   */
  readonly targetKey?: string | undefined;
}

/**
 * A plain `<input>` that emits PB-4923's editing lifecycle (AC5).
 *
 * The widget editors are full of raw `<input>` elements carrying their own
 * grid/table styling and their own focus refs, and swapping them for the shared
 * `TextInput` would change how they look. This exists so the lease can be added
 * to them as a one-word rename instead: everything else about the element —
 * every attribute, the className, the ref — passes straight through.
 *
 * A caller's own `onFocus`/`onBlur` still run. The lease goes FIRST on focus so
 * the claim is registered before any handler that might re-render, and first on
 * blur so the release is not lost if a handler throws.
 */
export const LeasedInput = forwardRef<HTMLInputElement, LeasedInputProps>(
  function LeasedInput({ targetKey, onFocus, onBlur, ...rest }, ref) {
    const lease = useFieldEditSession(targetKey);
    return (
      // eslint-disable-next-line ds/no-hand-built -- kept: the lease wrapper passes the caller's own grid/table input through unchanged (className, ref, every attribute); a DS field would restyle it.
      <input
        {...rest}
        ref={ref}
        onFocus={(event: FocusEvent<HTMLInputElement>) => {
          lease.onFocus();
          onFocus?.(event);
        }}
        onBlur={(event: FocusEvent<HTMLInputElement>) => {
          lease.onBlur();
          onBlur?.(event);
        }}
      />
    );
  },
);
